import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import Slider from 'react-slick';
import { Container, Section } from 'components/shared';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useDeviceWidth from 'utils/hooks/useDeviceWidth';
import styled from 'styled-components';
import cx from 'classnames';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useCountryValue } from 'stores/country';
import { Card } from 'components/components/molecules';
import { getSlidesData } from './getSlidesData';
import { buildMarketListSchema } from './buildMarketListSchema';
import { isValidTitle } from 'utils/validateTitle';
import { getDefaultFontSize } from 'utils/getDefaultFontSize';
import { Heading, Copy, IconButton } from 'components/components/atoms';

const SliderContainer = styled.div`
  /* Mobile: avoid second row, visualize the borders */
  .slick-list {
    overflow: visible !important;
  }
  .slick-track {
    display: flex !important;
  }
  .slick-slide {
    height: auto;
  }
  .slick-slide > div {
    height: 100%;
  }
`;

/* === DESKTOP:inside of Container with a "peek" to rigth === */
const DesktopPeek = styled.div`
  --peek: 72px; /* px for next card */
  --gutter: 12px; /* padding per slide */

  position: relative;
  overflow: hidden; /* avoid carousel goes throught the width of the screen */
  padding-right: var(--peek);
  margin-right: calc(var(--peek) * -1);

  .slick-list {
    overflow: visible; /* for the peek */
  }

  .slick-slide {
    padding: 0 var(--gutter);
    height: auto;
  }
  .slick-slide > div {
    height: 100%;
  }
`;

const Cards = ({ fields }) => {
  const [{ country }] = useCountryValue();

  // Stable identity: the `|| []` fallback would otherwise mint a new array on
  // every render, and slidesData now memoises on this.
  const cardsViewModel = useMemo(
    () => getFieldValue(fields, 'cardsViewModel') || [],
    [fields],
  );
  const cardsSchema = getFieldValue(fields, 'cardsSchema');
  const title = getFieldValue(fields, 'title');
  const description = getFieldValue(fields, 'description');
  const blueBackground = getFieldValue(fields, 'blueBackground');

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const deviceWidth = useDeviceWidth();
  const isMobile = ['sm', 'xs', 'md'].includes(breakpoint);

  const mobileCardWidth = useMemo(
    () => Math.round(deviceWidth * 0.85),
    [deviceWidth],
  );

  const [desktopItemWidth, setDesktopItemWidth] = useState(undefined);
  useEffect(() => {
    if (!isMobile) {
      const font = getDefaultFontSize();
      setDesktopItemWidth(font <= 18 ? 400 : undefined);
    }
  }, [isMobile]);

  const [position, setPosition] = useState(0);

  // Derived during render rather than held in state and refreshed from an
  // effect. As state, a country change landed one render before the cards it
  // selects: the URL was already /us while this still held both countries, so
  // for that render the page published an ItemList of all 22 markets under the
  // US locale. Long enough for a rendering crawler to snapshot it, which is what
  // made /us and /ca look like they carried two competing lists (MTTMY-2188).
  const slidesData = useMemo(
    () => getSlidesData(cardsViewModel, country),
    [cardsViewModel, country],
  );

  // Derived from slidesData, not from country, so the published list is always
  // the cards this render actually produced.
  const marketListSchema = useMemo(
    () => buildMarketListSchema(cardsSchema, slidesData, country),
    [cardsSchema, slidesData, country],
  );

  const sliderRef = useRef(null);

  // The carousel still has to rewind when the set of markets changes; only the
  // derivation moved out of the effect.
  useEffect(() => {
    setPosition(0);
    requestAnimationFrame(() => sliderRef.current?.slickGoTo(0, true));
  }, [slidesData]);

  const onFocusChange = useCallback(
    (index) => {
      if (index < slidesData.length - 2) {
        setPosition(index);
        sliderRef.current?.slickGoTo(index);
      }
    },
    [slidesData.length],
  );

  /* SETTINGS */
  const desktopSettingsVarWidth = {
    dots: false,
    infinite: false,
    speed: 450,
    cssEase: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    arrows: false,
    swipeToSlide: true,
    afterChange: (idx) => setPosition(idx),
  };

  const desktopSettingsFixed = {
    dots: false,
    infinite: false,
    speed: 450,
    cssEase: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    swipeToSlide: true,
    afterChange: (idx) => setPosition(idx),
  };

  const mobileSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: false,
    centerMode: true,
    centerPadding: '7.5%',
    arrows: false,
    swipeToSlide: true,
    afterChange: (idx) => setPosition(idx),
  };

  const handlePrev = () => sliderRef.current?.slickPrev();
  const handleNext = () => sliderRef.current?.slickNext();

  const desktopPeekRef = useRef(null);
  const [desktopContainerWidth, setDesktopContainerWidth] = useState(1200);

  useEffect(() => {
    if (desktopPeekRef.current) {
      const el = desktopPeekRef.current;
      setDesktopContainerWidth(el.offsetWidth);
    }
  }, [desktopPeekRef]);

  const visibleDesktop = desktopItemWidth * 3 < desktopContainerWidth ? 3 : 2;
  const atStart = position === 0;
  const atEndDesktop =
    position >= Math.max(0, slidesData.length - visibleDesktop);
  const atEndMobile = position >= Math.max(0, slidesData.length - 1);

  return (
    <Section
      id='cards'
      className={cx({
        'bg-light-blue': blueBackground,
        'bg-white': !blueBackground,
      })}
      tabIndex='-1'
    >
      {/* The markets this page shows, and only those: the item nodes are built
          server-side in GoogleStructuredDataService.GetMarketCardsSchemaModel
          over every market, then narrowed here to the ones rendered above. */}
      {marketListSchema && (
        <Helmet>
          <script defer type='application/ld+json'>
            {JSON.stringify(marketListSchema)}
          </script>
        </Helmet>
      )}
      <Container className='lg:px-5'>
        {isValidTitle(title) && (
          <Heading data-aos='fade-down' as='h2'>
            {title}
          </Heading>
        )}
        <Copy data-aos='fade-down' data-aos-delay='100' data-aos-offset='100'>
          {description}
        </Copy>

        {/* DESKTOP */}
        {!isMobile && (
          <DesktopPeek className='w-full' ref={desktopPeekRef}>
            <Slider
              ref={sliderRef}
              {...(desktopItemWidth
                ? desktopSettingsVarWidth
                : desktopSettingsFixed)}
            >
              {slidesData.map((c, i) => (
                <div
                  key={`${c.title}-${c.subtitle}-${i}`}
                  className='p-2 md:pb-16 md:pt-4'
                  onFocus={() => onFocusChange(i)}
                  style={
                    desktopItemWidth ? { width: desktopItemWidth } : undefined
                  }
                >
                  <Card
                    card={c}
                    favoritesList={[]}
                    data-aos='fade-left'
                    data-aos-delay={i * 150}
                  />
                </div>
              ))}
            </Slider>

            <IconButton
              onClick={handlePrev}
              className={cx(
                'hidden md:flex absolute left-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 ml-4',
                { 'opacity-0 pointer-events-none': atStart },
              )}
              icon='arrow-left'
              idIcon='cards'
              color='white'
              circled
              shadowed
              buttonLabel='Previous story panel.'
              title='Left arrow icon'
            />
            <IconButton
              onClick={handleNext}
              className={cx(
                'hidden md:flex absolute right-0 bottom-0 top-0 m-auto mr-8 z-20 transition-opacity duration-200',
                { 'opacity-0 pointer-events-none': atEndDesktop },
              )}
              icon='arrow-right'
              idIcon='cards'
              color='white'
              circled
              shadowed
              buttonLabel='Next story panel.'
              title='Right arrow icon'
            />
          </DesktopPeek>
        )}
      </Container>

      {/* MOBILE */}
      {isMobile && (
        <SliderContainer className='relative w-full'>
          <Slider ref={sliderRef} {...mobileSettings}>
            {slidesData.map((c, i) => (
              <div
                key={`${c.title}-${c.subtitle}-${i}`}
                className='p-2 md:pb-16 md:pt-4'
                onFocus={() => onFocusChange(i)}
                style={{ width: mobileCardWidth }}
              >
                <Card
                  card={c}
                  favoritesList={[]}
                  data-aos='fade-left'
                  data-aos-delay={i * 150}
                />
              </div>
            ))}
          </Slider>

          <IconButton
            onClick={handlePrev}
            className={cx(
              'ml-4 absolute left-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200',
              {
                'opacity-0 pointer-events-none':
                  slidesData.length <= 1 || atStart,
              },
            )}
            icon='arrow-left'
            idIcon='cards'
            color='white'
            circled
            shadowed
            hoverState
            buttonLabel='Previous story panel.'
            title='Left arrow icon'
            small
          />
          <IconButton
            onClick={handleNext}
            className={cx(
              'mr-3 absolute right-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200',
              {
                'opacity-0 pointer-events-none':
                  slidesData.length <= 1 || atEndMobile,
              },
            )}
            icon='arrow-right'
            idIcon='cards'
            color='white'
            circled
            shadowed
            hoverState
            buttonLabel='Next story panel.'
            title='Right arrow icon'
            small
          />
        </SliderContainer>
      )}
    </Section>
  );
};

export default Cards;
